import React, { useState, useEffect, useMemo, useCallback, lazy } from "react";
import {
  Menu,
  Avatar,
  Badge,
  Dropdown,
  Button,
  Drawer,
  Divider,
  Grid,
} from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash.get";
import { BASE_USER, BASE_CHECKER } from "@/constants/roles";
import {
  setSharedValue,
  showPanel,
  getLogoForAccount,
} from "@/pages/shared/shared.reducer";
import panels from "@/constants/panels";
import { getCurrentUserProfile } from "@/common/user-profile/user-profile-reducer";
import PanelContainer from "@/common/panel";
import { useNavigationItems } from "@/common/navigation/navigation.items";
import { logOut as logOutAction, getNotifications as getNotificationsAction } from "@/pages/login/login.reducer";
import Notifications from "@/common/notifications";
import { MenuTitle } from "@/common/navigation/menu-title";
import { MenuItem } from "@/common/navigation/menu-item";
import SystemEnvironmentBadge from "@/common/navigation/system-environment-badge";
import HelpCentreButton from "@/common/help-centre/help-button";
import { FormProvider } from "@/common/form-context";
import PanelLazyLoader from "@/common/panel-lazy-loader";
import { useAppearanceTheme } from "@/theme/theme-provider";
import DMSLogo from "/images/DMS-Icon.png";
import AccountLogo from "/images/loginlogo.png";
import '@/common/navigation/navigation-theme.less';

const UserProfile = lazy(() => import("@/common/user-profile/user-profile"));

const TOUR_TARGETS = {
  userProfile: "tour-user-profile",
  uploadDocuments: "tour-upload-documents",
  helpCentre: "tour-help-centre",
};

const NO_LOGOS = [];
const NO_PERMISSIONS = [];

const NavigationComponent = (props) => {
  const dispatch = useDispatch();
  const { appearanceTheme } = useAppearanceTheme();
  const isDarkMode = appearanceTheme === "dark";

  const userName = useSelector((state) => get(state.userDetails, "info.username"));
  const currentSiteId = useSelector((state) => get(state.shared, "siteId"));
  const listOfLogo = useSelector((state) => state.shared.accountLogo) || NO_LOGOS;
  const defaultSiteId = useSelector((state) => get(state.userDetails, "setting.defaultSiteId"));
  const permissions = useSelector((state) => state.userDetails.permissions) || NO_PERMISSIONS;
  const notificationsTotalCount = useSelector((state) => get(state.userDetails, "notifications.totalCount")) || 0;

  const logOut = useCallback(() => dispatch(logOutAction()), [dispatch]);
  const getLogo = useCallback(
    (accountId, theme) => dispatch(getLogoForAccount(accountId, theme)),
    [dispatch]
  );
  const getNotifications = useCallback(() => dispatch(getNotificationsAction()), [dispatch]);
  const setActiveReportKey = useCallback((key) => dispatch(setSharedValue("reportKey", key)), [dispatch]);
  const getCurrentUserInfo = useCallback(() => {
    dispatch(getCurrentUserProfile());
    dispatch(showPanel(panels.userProfile));
  }, [dispatch]);
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1920 : window.innerWidth
  );
  const updateWindowDimensions = useCallback(() => {
    setViewportWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", updateWindowDimensions);
    return () => {
      window.removeEventListener("resize", updateWindowDimensions);
    };
  }, [updateWindowDimensions]);

  const currentDashboard = useSelector(
    (state) => state.dashboardPage.currentDashboard
  );
  const maxUserRole = useSelector((state) => state.userDetails.maxUserRole);

  const current = location.pathname;
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const sites = useSelector((state) => state.dashboardPage.sites);

  const getTourSteps = useCallback((steps) => {
    if (
      !steps ||
      (currentDashboard && !currentDashboard === "documentDashboard")
    ) {
      return [];
    }
    return [
      ...steps,
      {
        title: "User Profile",
        description: "Click here to view and edit your user profile, including your personal information and settings." +
                      " Reset Password (if not an Active Directory Profile) and Leave Replacement can be set here.",
        target: () => document.getElementById(TOUR_TARGETS.userProfile),
      },
      maxUserRole && ![BASE_USER, BASE_CHECKER].includes(maxUserRole) ? {
        title: "Upload Documents",
        description: "Click here to upload new documents and drawings.",
        target: () => document.getElementById(TOUR_TARGETS.uploadDocuments),
      } : null,
      {
        title: "More Help and Support",
        description: "Click here to access more help, manuals and support resources.",
        target: () => document.getElementById(TOUR_TARGETS.helpCentre),
      },
    ].filter(Boolean);
  }, [currentDashboard, maxUserRole]);

  const getAccountLogo = useCallback(() => {
    const site =
      currentSiteId !== 0
        ? sites.find((x) => x.siteId === currentSiteId)
        : null;
    if (site) {
      const currentLogo = listOfLogo.find(
        (logo) => logo.accountId === site.accountId
      );
      if (currentLogo) {
        const themedLogo = isDarkMode ? currentLogo.darkLogo : currentLogo.logo;
        return themedLogo || currentLogo.logo || AccountLogo;
      }
      return AccountLogo;
    }
    return AccountLogo;
  }, [currentSiteId, isDarkMode, listOfLogo, sites]);

  const accountLogoUrl = getAccountLogo();

  useEffect(() => {
    if (currentSiteId === 0) {
      return;
    }
    const site = sites.find((x) => x.siteId === currentSiteId);
    if (!site) {
      return;
    }
    const theme = isDarkMode ? "Dark" : "Light";
    const logoKey = isDarkMode ? "darkLogo" : "logo";
    const hasLogo = listOfLogo.some(
      (logo) => logo.accountId === site.accountId && logo[logoKey] !== undefined
    );
    if (!hasLogo) {
      getLogo(site.accountId, theme);
    }
  }, [currentSiteId, isDarkMode, listOfLogo, sites, getLogo]);

  useEffect(() => {
    getNotifications();
  }, [getNotifications]);

  const handleClick = () => {
    setShowMobileMenu(false);
  };
  const [menuOpenState, setMenuOpenState] = useState({});
  
  const getSubMenu = useCallback((item, shouldSetIcon, returnComponent) => {
    const itemKey = item.key || item.title;
    const isOpen = menuOpenState[itemKey] || false;
    const icon = isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
    const iconColor = isOpen ? "var(--dms-nav-text)" : "var(--dms-nav-text-muted)";

    const onClick = (url) => {
      if (!url) {
        return;
      }
      navigate(url);
    };

    const handleVisibleChange = (open) => {
      setMenuOpenState(prevState => ({
        ...prevState,
        [itemKey]: open
      }));
    };

    const menuItems = item.items
      .filter(Boolean)
      .map((child, index, array) => {
        if (!child) return null;
        const isLastItem = index === array.length - 1;

        // Handle nested submenus
        if (child.items) {
          return [
            {
              icon: child.icon && <i className={child.icon} />,
              popupClassName: 'dms-navigation-menu-popup',
              label: (
                <div className="menu-item display-center">
                  <span>{child.title}</span>
                </div>
              ),
              key: child.key,
              children: child.items
                .map((subChild, subIndex, subArray) => {
                  const isLastSubItem = subIndex === subArray.length - 1;
                  return [
                    {
                      icon: subChild.icon && <i className={subChild.icon} />,
                      label: (
                        <div className="submenu-item">{subChild.title}</div>
                      ),
                      key: subChild.key,
                      onClick: () => onClick(subChild.url),
                    },
                    !isLastSubItem && { type: "divider" },
                  ].filter(Boolean);
                })
                .flat(),
            },
            !isLastItem && { type: "divider" },
          ].filter(Boolean);
        }

        // Regular menu items
        return [
          MenuItem(item, child, child.key ? setActiveReportKey : false, (url) =>
            onClick(url)
          ),
          !isLastItem && { type: "divider" },
        ].filter(Boolean);
      })
      .flat()
      .filter(Boolean);

    const isUnderLine =
      item.items &&
      item.items.some(
        (child) =>
          child &&
          (child.url === current ||
            (child.items &&
              child.items.some((grandchild) => grandchild.url === current)))
      );

    if (returnComponent) {
      // For submenu icon in return component
      const subMenuIcon = isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line";
      const subMenuIconColor = isOpen ? "var(--dms-nav-text)" : "var(--dms-nav-text-muted)";
      
      return {
        key: item.key || item.title,
        popupClassName: 'dms-navigation-menu-popup',
        label: (
          <div className="menu-item display-center">
            {MenuTitle(
              item,
              item.items.length > 0,
              !showMobileMenu && subMenuIcon,
              subMenuIconColor,
              false,
              false
            )}
          </div>
        ),
        children: menuItems,
        onClick: () => onClick(item.url),
        onOpenChange: handleVisibleChange,
      };
    }

    return {
      key: item.key || item.title,
      popupClassName: 'dms-navigation-menu-popup',
      label: (
        <div className="menu-item">
          {MenuTitle(
            item,
            shouldSetIcon,
            !showMobileMenu && icon,
            iconColor,
            true,
            isUnderLine
          )}
        </div>
      ),
      children: menuItems,
    };
  }, [current, menuOpenState, navigate, setActiveReportKey, showMobileMenu]);

  const curentSiteByLast =
    sites && sites.length > 0 && currentSiteId
      ? sites.find((item) => item.siteId === currentSiteId)
      : null;
  const currentSite =
    sites && sites.length > 0
      ? curentSiteByLast
        ? curentSiteByLast
        : sites.find((item) => item.siteId === defaultSiteId) || sites[0]
      : null;

  const permissionForCurrentSite = permissions.find(
    (item) => item.siteId === currentSite.siteId
  );
  
  // Use the hook that internally reads Redux to build navigation
  const navItems = useNavigationItems();
  const menuItems = useMemo(() => {
    return navItems
      .filter(Boolean)
      .map((item) => getSubMenu(item, true));
  }, [navItems, getSubMenu]);

  const [drawerOpenKeys, setDrawerOpenKeys] = useState([]);
  const drawerRootKeys = useMemo(
    () => (menuItems || []).map((i) => i && i.key).filter(Boolean),
    [menuItems]
  );

  const onDrawerOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => !drawerOpenKeys.includes(key));
    if (!drawerRootKeys.includes(latestOpenKey)) {
      setDrawerOpenKeys(keys);
    } else {
      setDrawerOpenKeys(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  const dropdownMenuItems = [
    {
      label: (
        <div className="menu-item display-center">
          <i className="ri-user-6-line" />
          <div style={{ marginLeft: "4px" }}>My Profile</div>
        </div>
      ),
      key: "profile",
      onClick: () => getCurrentUserInfo(),
    },
    {
      label: (
        <div className="menu-item display-center">
          <i className="ri-logout-box-line" />
          <div style={{ marginLeft: "4px" }}>Sign Out</div>
        </div>
      ),
      key: "signout",
      onClick: () => logOut(),
    },
  ];

  const renderNotificationsButton = () => (
    <Badge
      count={notificationsTotalCount}
      size="small"
      offset={[-10, 5]}
      style={{ borderColor: "transparent" }}
    >
      <div onClick={() => setShowNotifications(true)}>
        <Avatar
          size={36}
          icon={
            <i
              style={{ fontSize: 16 }}
              className="ri-notification-3-line"
            ></i>
          }
        ></Avatar>
      </div>
    </Badge>
  );

  const renderUserDropdown = ({ textColor, arrowInside = false, hideRoleOnAllSitesDashboard = false }) => (
    <Dropdown menu={{ items: dropdownMenuItems }} classNames={{ root: 'dms-navigation-dropdown' }}>
      <div
        className="display-center"
        style={{ flexDirection: "row", ...(textColor ? { color: textColor } : {}) }}
      >
        <div style={{ flexDirection: "column" }}>
          <div className="body-large-medium">{userName}</div>
          <div className="body-small-roman">
            {hideRoleOnAllSitesDashboard && current === '/all-sites-dashboard' ? (
              <></>
            ) : (
              permissionForCurrentSite && permissionForCurrentSite.roleName
            )}
            {arrowInside && <i className="ri-arrow-down-s-line"></i>}
          </div>
        </div>
        {!arrowInside && <i className="ri-arrow-down-s-line"></i>}
      </div>
    </Dropdown>
  );

  const onClose = () => {
    setShowMobileMenu(false);
  };

  const showDrawer = () => {
    setShowMobileMenu(true);
  };

  return (
    <div className="navigation">
      <FormProvider>
        <PanelContainer
          id={panels.userProfile}
          title={"My Profile"}
          content={
            <PanelLazyLoader
              component={UserProfile}
              panelId={panels.userProfile}
              isProfile={true}
              readOnly={false}
            />
          }
          className={"panel document-detail"}
        />
      </FormProvider>

      <Notifications
        visible={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
      <div
        className="display-center navigation-inner-container"
        style={{ display: "flex" }}
      >
        <div className="navigation-branding">
          <div
            className="navigation-logo-container"
            style={{ minWidth: "180px" }}
          >
            <Link to={"/"}>
              <img
                alt="dmsLogo"
                src={DMSLogo}
                loading="lazy"
                className="dms-logo mb-1"
                height="35px"
              />
            </Link>
            <Divider orientation="vertical" className="logo-divider" />
            <Link to={"/"}>
              <img
                alt="accountLogo"
                src={accountLogoUrl || AccountLogo}
                className="account-logo mb-1"
                width={150}
                height={40}
              />
            </Link>
          </div>
          <SystemEnvironmentBadge />
        </div>
        <Menu
          className="menu-navigation"
          theme="dark"
          onClick={handleClick}
          mode="horizontal"
          disabledOverflow
          items={menuItems}
        />
        {maxUserRole && ![BASE_USER, BASE_CHECKER].includes(maxUserRole) &&
          window.location.pathname !== "/upload-document" &&
          screens.lg && (
            <Link
              id={TOUR_TARGETS.uploadDocuments}
              className="menu-navigation-button"
              to="/upload-document"
              style={{ fontSize: 14 }}
            >
              <div className="upload-document-button display-center">
                <i
                  style={{ fontSize: 16, paddingRight: 6 }}
                  className="ri-upload-line"
                ></i>
                Upload Documents
              </div>
            </Link>
          )}

        <div className="h-100 display-center user-info">
          <div>
            {renderNotificationsButton()}
          </div>
          <div>
            <HelpCentreButton
              steps={getTourSteps(props.steps)}
              tourTargetId={TOUR_TARGETS.helpCentre}
              className="help-button"
            />
          </div>
          <div style={{ paddingLeft: 16 }}>
            <div id={TOUR_TARGETS.userProfile}>
              <Avatar
                size={36}
                icon={<i style={{ fontSize: 16 }} className="ri-user-6-line"></i>}
                onClick={() => getCurrentUserInfo()}
              ></Avatar>
            </div>
          </div>
          <div style={{ paddingLeft: 12 }}>
            {renderUserDropdown({ hideRoleOnAllSitesDashboard: true })}
          </div>
        </div>
        <Button
          type="default"
          onClick={showDrawer}
          className="display-center call-mobile-menu"
        >
          <i className="ri-menu-line"></i>
          Menu
        </Button>
      </div>
      <Drawer
        placement="right"
        onClose={onClose}
        open={showMobileMenu}
        rootClassName="mobile-menu"
        footer={maxUserRole && ![BASE_USER, BASE_CHECKER].includes(maxUserRole) &&
          window.location.pathname !== "/upload-document" &&
          (viewportWidth >= 768 && viewportWidth < 1024) ? (
            <Link
              className="menu-navigation-button"
              to="/upload-document"
              style={{ fontSize: 14 }}
            >
              <div className="upload-document-button display-center">
                <i
                  style={{ fontSize: 16, paddingRight: 6 }}
                  className="ri-upload-line"
                ></i>
                Upload Documents
              </div>
            </Link>
          ) : null
        }
        extra={
          <>
            Menu
            <Button onClick={onClose}>
              <i className="ri-close-line"></i>
              Close
            </Button>
          </>
        }
      >
        
        <div
          className="w-100 display-center user-info"
          style={{ justifyContent: "space-between", marginBottom: 20 }}
        >
          <div className="display-center">
            <div>
              <Avatar
                size={36}
                icon={
                  <i style={{ fontSize: 16 }} className="ri-user-6-line"></i>
                }
              ></Avatar>
            </div>
            <div style={{ paddingLeft: 12 }}>
              {renderUserDropdown({ textColor: "var(--dms-nav-text)", arrowInside: true })}
            </div>
          </div>
          <div>
            {renderNotificationsButton()}
          </div>
        </div>
        <Menu
          className="menu-navigation custom-border-menu"
          theme="dark"
          onClick={handleClick}
          mode="inline"
          triggerSubMenuAction="click"
          inlineIndent={0}
          openKeys={drawerOpenKeys}
          onOpenChange={onDrawerOpenChange}
          items={menuItems}
        />
      </Drawer>
    </div>
  );
};

export default NavigationComponent;
